import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from './useDebouncedValue';

/** Same 300ms every search on this app debounces by. */
const DEBOUNCE_MS = 300;

export interface MapCategorySearch<TItem, TFilters> {
  filters: TFilters;
  setFilters: Dispatch<SetStateAction<TFilters>>;
  items: TItem[];
  totalCount: number;
  loading: boolean;
}

/**
 * One map category's own search: its filters, its results, and the debounced
 * fetch that keeps the two in step.
 *
 * Extracted because the map now has four of these (machinery, accessories,
 * operators, services) and the mechanics are identical every time -- filters
 * in state, debounce, fetch, drop the answer if the tab changed underneath.
 * Written out four times it is eighty lines of copy where the only thing that
 * differs is which function to call, and the fourth copy is where somebody
 * forgets the `cancelled` guard.
 *
 * `active` is what keeps a background tab silent: switching away stops the
 * fetching without unmounting anything, so coming back still has its results
 * and its filters exactly as they were left.
 */
export function useMapCategorySearch<TItem, TFilters>(
  active: boolean,
  initialFilters: TFilters,
  search: (filters: TFilters) => Promise<{ items: TItem[]; totalCount: number }>
): MapCategorySearch<TItem, TFilters> {
  const [filters, setFilters] = useState<TFilters>(initialFilters);
  const [items, setItems] = useState<TItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Held in a ref rather than listed as a dependency below. Every caller
  // passes a module-level service function today, but the day one passes an
  // inline arrow it would be a new value on every render and the effect would
  // refetch for ever -- a bug that shows up as a hammered API, not as a
  // broken screen, so it is worth making impossible rather than commenting on.
  const searchRef = useRef(search);
  searchRef.current = search;

  const debouncedFilters = useDebouncedValue(filters, DEBOUNCE_MS);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    setLoading(true);
    searchRef
      .current(debouncedFilters)
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTotalCount(result.totalCount);
      })
      .catch(() => {
        // An empty list rather than a thrown error: this feeds a map and a
        // results sheet, and the sheet's own empty state ("nada por aquí")
        // is a better answer to a failed search than a red banner over a map.
        if (cancelled) return;
        setItems([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, debouncedFilters]);

  return { filters, setFilters, items, totalCount, loading };
}
